import React from 'react';

// -----------------------------------------------------------------------------
// SDMark — icon-only brand mark. Uses the OFFICIAL Sustainability Decoded
// logo paths (two mirrored teardrop shapes) from the brand SVG files. The
// tile background + mark colours are customisable so the same component
// renders the three canonical variants:
//   • brand  → green tile, white marks (default; matches Logo-07 asset)
//   • dark   → black tile, white top + green bottom (matches Logo-08 asset)
//   • light  → white tile, black top + green bottom (matches Logo-09 asset)
// -----------------------------------------------------------------------------
const SD_MARK_PATH_TOP    = 'M452.9,300a128.6,128.6,0,0,0-42.7,249.86,11.19,11.19,0,0,0,11.62-2.71L611.21,357.77c21.32-21.32,6.22-57.77-23.93-57.77Zm102.75,51.25-31.53,31.51-117,117A84.67,84.67,0,0,1,452.9,343.9H563Z';
const SD_MARK_PATH_BOTTOM = 'M547.1,700a128.6,128.6,0,0,0,42.7-249.86,11.19,11.19,0,0,0-11.62,2.71L388.79,642.23C367.47,663.55,382.57,700,412.72,700ZM444.35,648.75l31.53-31.51,117-117A84.67,84.67,0,0,1,547.1,656.1H437Z';

export function SDMark({ size = 32, bg = '#44C349', markTop = '#FFFFFF', markBottom, radius = 0.22, className = '' }) {
  // `radius` is a fraction of `size` (0..0.5) so the corner-roundness scales
  // sensibly at any icon size. Default 0.22 matches the app's rounded-tile look.
  const r = size * radius;
  const bottom = markBottom || markTop;
  return (
    <svg width={size} height={size} viewBox="0 0 1000 1000" fill="none" aria-hidden="true" className={className}>
      <rect x="0" y="0" width="1000" height="1000" rx={r * (1000 / size)} fill={bg} />
      <path d={SD_MARK_PATH_TOP}    fill={markTop} />
      <path d={SD_MARK_PATH_BOTTOM} fill={bottom} />
    </svg>
  );
}

export function SDLogo({ size = 28, color = '#0A0A0A', accent = '#4CD964', variant }) {
  // Named variant presets so callers don't have to memorise hex/accent combos.
  //   variant="brand" → black tile + white top + green bottom (the canonical mark)
  //   variant="light" → white tile + black top + green bottom  (Logo-09)
  //   variant="green" → green tile + white marks               (single-tone accent)
  //   default         → legacy tile colouring via `color` + `accent`
  const preset =
    variant === 'brand' ? { tile: '#000000', top: '#FFFFFF', bottom: '#44C349', wordText: '#0A0A0A', wordDot: '#44C349' }
    : variant === 'light' ? { tile: '#FFFFFF', top: '#0A0A0A', bottom: '#44C349', wordText: '#0A0A0A', wordDot: '#44C349' }
    : variant === 'green' ? { tile: '#44C349', top: '#FFFFFF', bottom: '#FFFFFF', wordText: '#0A0A0A', wordDot: '#44C349' }
    : { tile: color, top: accent, bottom: accent, wordText: color, wordDot: accent };
  return (
    <div className="flex items-center gap-2.5">
      <SDMark size={size + 4} bg={preset.tile} markTop={preset.top} markBottom={preset.bottom} />
      <span style={{ color: preset.wordText }} className="font-semibold text-[16px] tracking-tight">
        SD<span style={{ color: preset.wordDot }}>·</span>Pivot
      </span>
    </div>
  );
}

export function LinkedInIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="#FFFFFF">
      <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.05-1.86-3.05-1.86 0-2.15 1.45-2.15 2.95v5.67H9.34V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zm1.78 13.02H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z" />
    </svg>
  );
}

export function Chip({ children, on, onClick, variant = 'green', testId }) {
  const variantClass =
    variant === 'amber' ? 'sd-chip sd-chip-amber'
    : variant === 'purple' ? 'sd-chip sd-chip-purple'
    : 'sd-chip';
  return (
    <button
      type="button"
      className={`${variantClass} ${on ? 'sd-chip-on' : ''}`}
      onClick={onClick}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

export function Avatar({ name, size = 40 }) {
  const initials = (name || 'SD').split(' ').filter(Boolean).slice(0, 2).map(s => s[0]?.toUpperCase()).join('') || 'SD';
  return (
    <div
      style={{ width: size, height: size, fontSize: size * 0.4 }}
      className="rounded-full bg-gradient-to-br from-[#4CD964] to-[#3FB854] text-white font-semibold flex items-center justify-center shadow-sm"
    >
      {initials}
    </div>
  );
}

export function Spinner({ size = 28, color = '#4CD964' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 50 50" className="animate-spin-slow">
      <circle cx="25" cy="25" r="20" stroke="#E3E5E3" strokeWidth="4" fill="none" />
      <path d="M 25 5 A 20 20 0 0 1 45 25" stroke={color} strokeWidth="4" fill="none" strokeLinecap="round" />
    </svg>
  );
}

export function LoadingDots() {
  return (
    <div className="flex gap-1.5 items-center">
      <span className="w-1.5 h-1.5 rounded-full bg-current sd-loading-dot" />
      <span className="w-1.5 h-1.5 rounded-full bg-current sd-loading-dot" />
      <span className="w-1.5 h-1.5 rounded-full bg-current sd-loading-dot" />
    </div>
  );
}

export function ScoreRing({ score, size = 96 }) {
  const radius = size / 2 - 8;
  const circumference = 2 * Math.PI * radius;
  const pct = Math.min(10, Math.max(0, score)) / 10;
  const color = score >= 7 ? '#4CD964' : score >= 5 ? '#D4943A' : '#C94040';
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={radius} stroke="#E3E5E3" strokeWidth="6" fill="none" />
      <circle
        cx={size / 2} cy={size / 2} r={radius}
        stroke={color} strokeWidth="6" fill="none"
        strokeDasharray={`${circumference * pct} ${circumference}`}
        strokeDashoffset={circumference / 4}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" fontSize={size * 0.32} fontWeight="600" fill={color}>
        {Number(score || 0).toFixed(1)}
      </text>
    </svg>
  );
}
